<template>
<div> 
	<p class="font-thin text-3xl mb-2"> Interpolationsinställningar</p>
	<div class="w-full flex">
		<div class="w-4/5">
			<p class="text-grey-darker">
				Här finns möjligheten att lägga till egna interpolationsinställningar.<br>
				Ni kan välja vilka intervall som ska interpoleras för en specifik typ och vilken färg varje intervall ska ha.
			</p>
		</div>
		<div class="w-1/5 flex justify-end">
			<div>
				<button @click="openModal()" class="button no-outline shadow bg-green hover:bg-green-dark pr-2">
					Lägg till inställning
				</button>
			</div>
		</div>
	</div>
	<div class="w-full px-8 mt-6">

		<div class="w-full flex justify-end mb-2">
			<div class="relative w-1/4">
				<select v-model="selectedType" class="block appearance-none w-full bg-white border border-grey-light hover:border-grey px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline">
					<option :value="null">Visa alla</option>
					<option v-for="type in types" :key="type" :value="type" v-text="getTypeTitle(type)"></option>
				</select>
				<div class="pointer-events-none absolute pin-y pin-r flex items-center px-2 text-grey-darker">
					<svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
				</div>
			</div>
		</div>
		<div class="border border-grey-light bg-white rounded shadow">
			<div class="flex flex-col w-full">
				<table class="table mb-0 w-full">
					<thead>
						<tr>
							<th>Namn</th>
							<th>Aktiv</th>
							<th>Typ</th>
							<th>Färger</th>
							<th></th>
						</tr>
					</thead>
					<tbody v-for="setting in pageinatedSettings" :key="setting.id">
						<tr> 
							<td v-text="setting.title"></td>
							<td>
								<svg v-if="setting.active" class="w-4 h-4 text-green fill-current" viewBox="0 0 24 24"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
							</td>
							<td v-text="getTypeTitle(setting.type)"></td>
							<td v-text="setting.interpolation_colors.length + ' färger'"></td>
							<td>
								<button @click="openModal(setting)" class="button-sm no-outline text-grey-darkest border hover:bg-grey-light shadow">Redigera</button>
								<button v-if="!setting.active && !setting.default" @click="deleteSetting(setting.id)" class="ml-2 no-outline button-sm border hover:bg-grey-light shadow">
									<svg class="w-3 h-3 text-red-light fill-current" viewBox="0 0 24 24"><path d="M23.954 21.03l-9.184-9.095 9.092-9.174-2.832-2.807-9.09 9.179-9.176-9.088-2.81 2.81 9.186 9.105-9.095 9.184 2.81 2.81 9.112-9.192 9.18 9.1z"/></svg>
								</button>
								<button class="ml-2 no-outline button-sm border hover:bg-grey-light shadow" @click="expandSetting(setting.id)">
									<svg :class="[expandedSetting === setting.id ? '' : 'down', 'h-3 w-3 text-grey-darkest fill-current rotate']" viewBox="0 0 24 24"><path d="M0 16.67l2.829 2.83 9.175-9.339 9.167 9.339 2.829-2.83-11.996-12.17z"/></svg>
								</button>
							</td>
						</tr>
						<tr>
							<td v-if="expandedSetting === setting.id" colspan="5">
								<div class="flex px-4 py-2">
									<div class="w-1/5">
										<p><b>Beskrivning:</b></p>
										<p>{{setting.description}}</p>
									</div>
									<div class="w-2/5 px-2">
										<div class="border w-fit rounded-lg">
											<table class="table">
												<tbody>
													<tr>
														<td>Mjuka färgövergångar:</td>
														<td>
															<span :class="[setting.smooth_colors ? 'bg-green' : 'bg-grey', 'inline-block rounded-full ml-1 w-2 h-2']" style="margin-bottom:0.12rem"></span> 
														</td>
													</tr>
													<tr>
														<td>Interpolera över fältgränser:</td>
														<td>
															<span :class="[setting.interpolate_zero_values ? 'bg-green' : 'bg-grey', 'inline-block rounded-full ml-1 w-2 h-2']" style="margin-bottom:0.12rem"></span> 
														</td>
													</tr>
													<tr>
														<td>Interpolera nollvärden:</td>
														<td>
															<span :class="[setting.interpolate_over_borders ? 'bg-green' : 'bg-grey', 'inline-block rounded-full ml-1 w-2 h-2']" style="margin-bottom:0.12rem"></span> 
														</td>
													</tr>
													<tr>
														<td>Interpoleringsalgoritm:</td>
														<td><b v-text="getAlgorithmName(setting.interpolation_type)"></b></td>
													</tr>
													<tr>
														<td>Sökradie:</td>
														<td><b>{{setting.search_radius}} m </b></td>
													</tr>
													<tr>
														<td>Interpolationsfaktor:</td>
														<td><b v-text="setting.interpolation_factor"></b></td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>
									<div class="w-2/5 px-2">
										<div class="border rounded-lg overflow-hidden">
											<table class="table">
												<thead>
													<tr>
														<th>Min</th>
														<th>Max</th>
														<th>Färg</th>
													</tr>
												</thead>
												<tbody>
													<tr v-for="color in setting.interpolation_colors" :key="color.id">
														<td v-text="color.from"></td>
														<td v-text="color.to"></td>
														<td>
															<div class="w-6 h-2 rounded-full" :style="'background-color:' + color.Rgb +';'"></div>
														</td>
													</tr>
												</tbody>
											</table>

										</div>
									</div>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
			<div v-if="filteredSettings.length > nbrRows" class="flex justify-center py-4">
				<div class="border-2 rounded-lg overflow-hidden">
					<button 
						@click="activePage != 1 ? activePage-- : ''"
						:class="[activePage == 1 ? 'disabled' : '', 'px-4 py-2 no-outline text-grey-darkest border-r text-xl bg-grey-lighter hover:bg-grey-light']">
						<svg class="h-3 w-3 text-grey-darkest fill-current" viewBox="0 0 24 24"><path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z"/></svg>
					</button>
					<button 
						v-for="i in nbrPages" :key="i" @click="activePage = i" 
						:class="[activePage == i ? 'bg-grey-light' : 'bg-grey-lighter', 'px-4 py-2 no-outline text-grey-darkest border-r text-xl hover:bg-grey-light']" v-text="i">
					</button>
					<button 
						@click="activePage != nbrPages ? activePage++ : ''" 
						:class="[activePage == nbrPages ? 'disabled' : '', 'px-4 py-2 no-outline text-grey-darkest text-xl bg-grey-lighter hover:bg-grey-light']">
							<svg class="h-3 w-3 text-grey-darkest fill-current" viewBox="0 0 24 24"><path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z"/></svg>
					</button>
				</div>
			</div>
	</div>
	<InterpolationModal></InterpolationModal>
</div>
</template>

<script>

import InterpolationModal from '@/components/settings/InterpolationModal.vue'

import { mapGetters } from 'vuex'

export default {
	
	name: 'InterpolationSettings',

	components: {
		InterpolationModal
	},

	data() {
		return {
			selectedType: null,
            nbrRows: 8,
			activePage: 1,

			expandedSetting: null,
		}
	},

	watch: {
		selectedType() {
			this.activePage = 1
		}
	},

	computed: {
		...mapGetters({
			settings: 	'interpolationSettings/settings',
			allTypes: 	'analysis/types',
		}),

		types() {
			return [...new Set(this.settings.map(setting => setting.type))]
		},

		filteredSettings() {
			return this.settings.filter(setting => {
				if(this.selectedType) {
					return this.selectedType == setting.type
				}
				return true
			})
		},

		nbrPages() {
			return Math.ceil(this.filteredSettings.length / this.nbrRows)
		},

		pageinatedSettings() {
            let nbrElements = (this.activePage - 1) * this.nbrRows
            return this.filteredSettings.slice(nbrElements, nbrElements + this.nbrRows)
		},
	},

	methods: {
		/**
		 *	Opens the modal for creating/editing settings
		 *	The modal will be in 'create mode' if the setting is null
		 */
		openModal(setting = null) {
			bus.$emit('open-interpolation-setting-modal', setting)
		},

		/**
		 *	Will delete the settings with the provided id
		 *	Can only be deleted if it is not active
		 */
		deleteSetting(id) {
			if(confirm('Är du säker på att du vill ta bort inställningen?')) {
				this.$store.dispatch("interpolationSettings/DELETE_SETTING", id)
			}
		},

		/**
		 *	Will expand the provided setting if not already expanded
		 *	then it will close
		 *	If another setting is expanded already then that setting weill be collapsed
		 */
		expandSetting(id) {
			if(this.expandedSetting === id) {
				this.expandedSetting = null
			} else {
				this.expandedSetting = id
			}
		},

		getAlgorithmName(type) {
			if(type == 1) {
				return "Traditionell"
			} else if (type == 3) {
				return "Ny interpolationsteknik"
			}
		},

        getTypeTitle(slug) {
			if(slug === 'lerhaltskartan') {
				return 'Lerhaltskartan'
			} else if(slug === 'kalk_50_procent_cao') {
				return 'Kalk 50% CaO'				
			} else if(slug === 'magnesiumbehov') {
				return 'Magnesiumbehov'
			} else if(slug === 'avkastningsminskning') {
				return 'Avkastningsminskning'
			}
			let type = this.allTypes.find(type => {return type.slug === slug})
			return type ? type.title : 'Typ saknas'
		}
	}
}
</script>
<style>

	.rotate{
		-moz-transition: all 0.2s linear;
		-webkit-transition: all 0.2s linear;
		transition: all 0.2s linear;
	}

	.rotate.down{
		-ms-transform: rotate(180deg);
		-moz-transform: rotate(180deg);
		-webkit-transform: rotate(180deg);
		transform: rotate(180deg);
	}
	</style>